import { useCallback, useEffect, useRef } from 'react'

import { type ClipboardWriter, writeClipboard } from '../clipboard'

export interface UseCopyDepositAddressParams {
  copyToClipboard: ClipboardWriter
  /** Reports a successful write with its optional confirmation label. */
  onCopySuccess: (label?: string) => void
  /** Copy the address automatically while enabled, including when it changes. */
  autoCopyAddress: boolean
  /** Undefined while it's still generating. */
  address: string | undefined
}

/**
 * The Copy buttons' clipboard write + toast, plus the auto-copy when Transfer
 * opens and whenever the address changes.
 *
 * The returned write never rejects; `false` means the value wasn't copied.
 */
export function useCopyDepositAddress({
  copyToClipboard,
  onCopySuccess,
  autoCopyAddress,
  address,
}: UseCopyDepositAddressParams): (
  value: string,
  label?: string,
) => Promise<boolean> {
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const copyAddress = useCallback(
    async (value: string, label?: string): Promise<boolean> => {
      const ok = await writeClipboard(copyToClipboard, value)
      if (ok && mountedRef.current) {
        onCopySuccess(label)
      }
      return ok
    },
    [copyToClipboard, onCopySuccess],
  )

  // Keyed on the address, so a switch between chains sharing one doesn't
  // re-copy; cleared while disabled so re-enabling copies again.
  const copiedAddressRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!autoCopyAddress) {
      copiedAddressRef.current = undefined
      return
    }
    if (!address || copiedAddressRef.current === address) {
      return
    }
    copiedAddressRef.current = address
    void copyAddress(address)
  }, [autoCopyAddress, address, copyAddress])

  return copyAddress
}
